import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Box,
  Checkbox,
} from '@material-ui/core';

const getInitials = (string) => {
  if (!string) return '';
  const names = string.trim().split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

const CustomAvatar = ({ color = 'primary', children }) => {
  const isLead = color === 'success';
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: isLead ? '#E1EDC5' : '#EBF5FF',
        color: isLead ? '#4D7C0F' : '#1D4ED8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '13px',
      }}
    >
      {children}
    </div>
  );
};

const TenantCardItem = ({ tenant, showSelect, handleTenantSelect }) => {
  const user = tenant?.app_user?.user;
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim().toUpperCase() || 'TENANT';
  const isVerified = tenant?.app_user?.idverification == 2 || tenant?.app_user?.idverification == 3;

  return (
    <Card variant="outlined" style={{ borderRadius: 8, height: '100%', borderColor: '#E2E8F0' }}>
      <CardHeader
        avatar={
          <CustomAvatar color={tenant.is_lead ? 'success' : 'primary'}>
            {getInitials(fullName)}
          </CustomAvatar>
        }
        action={
          showSelect ? (
            <Checkbox
              disabled={!isVerified}
              onChange={handleTenantSelect}
              name={String(tenant.open_flow_sub_tenant_id)}
              color="primary"
            />
          ) : null
        }
        title={
          <Typography variant="body1" style={{ textTransform: 'capitalize', fontWeight: 600, color: '#1E293B' }}>
            {user?.title ? `${user.title}. ` : ''}
            {fullName}
          </Typography>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 16 }}>
        {tenant?.app_user?.opbrix_id && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>TLYFE ID:</b> {tenant?.app_user?.opbrix_id}
            </Typography>
          </Box>
        )}
        {user?.email && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Email:</b> {user.email}
            </Typography>
          </Box>
        )}
        {user?.contact_number && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Contact:</b> {user.contact_number}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const LandlordCardItem = ({ landlord, showSelect, handleLandlordSelect }) => {
  const fullName = `${landlord?.first_name || ''} ${landlord?.last_name || ''}`.trim().toUpperCase() || 'LANDLORD';

  return (
    <Card variant="outlined" style={{ borderRadius: 8, height: '100%', borderColor: '#E2E8F0' }}>
      <CardHeader
        avatar={
          <CustomAvatar color={landlord.is_lead ? 'success' : 'primary'}>
            {getInitials(fullName)}
          </CustomAvatar>
        }
        action={
          showSelect ? (
            <Checkbox
              onChange={handleLandlordSelect}
              name={String(landlord.open_flow_landlord_id)}
              color="primary"
            />
          ) : null
        }
        title={
          <Typography variant="body1" style={{ textTransform: 'capitalize', fontWeight: 600, color: '#1E293B' }}>
            {landlord?.title ? `${landlord.title}. ` : ''}
            {fullName}
          </Typography>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 16 }}>
        {landlord?.email && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Email:</b> {landlord.email}
            </Typography>
          </Box>
        )}
        {landlord?.contact_number && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Contact:</b> {landlord.contact_number}
            </Typography>
          </Box>
        )}
        {landlord?.postcode && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Postcode:</b> {landlord.postcode}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const GuarantorCardItem = ({ guarantor, showSelect, handleGuarantorSelect }) => {
  const fullName = `${guarantor?.first_name || ''} ${guarantor?.last_name || ''}`.trim().toUpperCase() || 'GUARANTOR';

  return (
    <Card variant="outlined" style={{ borderRadius: 8, height: '100%', borderColor: '#E2E8F0' }}>
      <CardHeader
        avatar={
          <CustomAvatar color="primary">
            {getInitials(fullName)}
          </CustomAvatar>
        }
        action={
          showSelect ? (
            <Checkbox
              onChange={handleGuarantorSelect}
              name={String(guarantor.open_flow_guarantor_id)}
              color="primary"
            />
          ) : null
        }
        title={
          <Typography variant="body1" style={{ textTransform: 'capitalize', fontWeight: 600, color: '#1E293B' }}>
            {guarantor?.title ? `${guarantor.title}. ` : ''}
            {fullName}
          </Typography>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 16 }}>
        {guarantor?.email && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Email:</b> {guarantor.email}
            </Typography>
          </Box>
        )}
        {guarantor?.phone_number && (
          <Box style={{ marginBottom: 4 }}>
            <Typography variant="body2" style={{ color: '#475569', fontSize: 13 }}>
              <b>Contact:</b> {guarantor.phone_number}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const FlowStakeHolders = ({ openFlow, showSelect, handleTenantSelect, handleLandlordSelect, handleGuarantorSelect }) => {
  const tenants = openFlow?.tenants || [];
  const landlords = openFlow?.open_flow_landlords || [];
  const guarantors = openFlow?.open_flow_guarantors || [];

  return (
    <Grid container spacing={3}>
      {/* Tenants */}
      <Grid item xs={12}>
        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#CBD5E1' }}>
          <CardHeader
            title={
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1E293B' }}>
                Tenants
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <Grid container spacing={2}>
              {tenants.map((tenant, index) => (
                <Grid item xs={12} sm={6} md={4} key={tenant.open_flow_sub_tenant_id || index}>
                  <TenantCardItem
                    tenant={tenant}
                    showSelect={showSelect}
                    handleTenantSelect={handleTenantSelect}
                  />
                </Grid>
              ))}
              {tenants.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" style={{ color: '#64748B' }}>No tenants added yet.</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Landlords */}
      <Grid item xs={12}>
        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#CBD5E1' }}>
          <CardHeader
            title={
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1E293B' }}>
                Landlords
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <Grid container spacing={2}>
              {landlords.map((landlord, index) => (
                <Grid item xs={12} sm={6} md={4} key={landlord.open_flow_landlord_id || index}>
                  <LandlordCardItem
                    landlord={landlord}
                    showSelect={showSelect}
                    handleLandlordSelect={handleLandlordSelect}
                  />
                </Grid>
              ))}
              {landlords.length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" style={{ color: '#64748B' }}>No landlords added yet.</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Guarantors */}
      {guarantors.length > 0 && (
        <Grid item xs={12}>
          <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#CBD5E1' }}>
            <CardHeader
              title={
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1E293B' }}>
                  Guarantors
                </Typography>
              }
            />
            <CardContent style={{ paddingTop: 0 }}>
              <Grid container spacing={2}>
                {guarantors.map((guarantor, index) => (
                  <Grid item xs={12} sm={6} md={4} key={guarantor.open_flow_guarantor_id || index}>
                    <GuarantorCardItem
                      guarantor={guarantor}
                      showSelect={showSelect}
                      handleGuarantorSelect={handleGuarantorSelect}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default FlowStakeHolders;
